import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import { Loader } from "rsuite";
import { TbHandClick } from "react-icons/tb";
import WaterMark from "../../../Components/WaterMark/WaterMark";
import { axInst } from "../../../Components/Login/Service";
import ModalMuiTable from "../../../Components/Modals/ModalMuiTable.jsx";

/* ================= API CALLS ================= */
const regionApi = (body) =>
  axInst.post("/realization/nfa_status_region_wise", body).then((r) => r.data);

const rsmApi = (body) =>
  axInst.post("/realization/nfa_status_rsm_wise", body).then((r) => r.data);

const spmApi = (body) =>
  axInst.post("/realization/nfa_status_spm_wise", body).then((r) => r.data);

const tsmApi = (body) =>
  axInst.post("/realization/nfa_status_tsm_wise", body).then((r) => r.data);

const dealerApi = (body) =>
  axInst.post("/realization/nfa_status_dealer_wise", body).then((r) => r.data);

const REGION_ORDER = ["North", "East", "West", "South"];

/* ================= COMPONENT ================= */
export const Nfa = () => {
  const { data: filterdata, submitted } = useSelector(
    (state) => state.filtersRealization,
  );

  const { data = {}, isLoading } = useQuery({
    queryKey: ["nfaStatusRegion", submitted],
    queryFn: () => regionApi(filterdata),
    refetchInterval: 300000,
    cacheTime: 0,
    staleTime: 0,
  });

  const [expanded, setExpanded] = useState({});
  const [open, setOpen] = useState(false);

  const [detailLoading, setDetailLoading] = useState(false);
  const [modalFilters, setModalFilters] = useState({});

  /* ================= DRILLDOWN ================= */
  const toggleRegion = async (region) => {
    if (expanded[region]) {
      const next = { ...expanded };
      delete next[region];
      setExpanded(next);
      return;
    }
    const rsm = await rsmApi({ ...filterdata, region: [region] });
    setExpanded((p) => ({ ...p, [region]: { rsm, expanded: {} } }));
  };

  const toggleRsm = async (region, rsm) => {
    const regionData = expanded[region];
    if (regionData.expanded[rsm]) {
      const next = { ...expanded };
      delete next[region].expanded[rsm];
      setExpanded(next);
      return;
    }
    const spm = await spmApi({
      ...filterdata,
      region: [region],
      Rsm_Name: [rsm],
    });
    setExpanded((p) => ({
      ...p,
      [region]: {
        ...p[region],
        expanded: { ...p[region].expanded, [rsm]: { spm, expanded: {} } },
      },
    }));
  };

  const toggleSpm = async (region, rsm, spm) => {
    const rsmBlock = expanded[region].expanded[rsm];
    if (rsmBlock.expanded[spm]) {
      const next = { ...expanded };
      delete next[region].expanded[rsm].expanded[spm];
      setExpanded(next);
      return;
    }
    const tsm = await tsmApi({
      ...filterdata,
      region: [region],
      Rsm_Name: [rsm],
      Spm_Name: [spm],
    });
    setExpanded((p) => ({
      ...p,
      [region]: {
        ...p[region],
        expanded: {
          ...p[region].expanded,
          [rsm]: {
            ...p[region].expanded[rsm],
            expanded: {
              ...p[region].expanded[rsm].expanded,
              [spm]: { tsm, expanded: {} },
            },
          },
        },
      },
    }));
  };

  const toggleTsm = async (region, rsm, spm, tsm) => {
    const spmBlock = expanded[region].expanded[rsm].expanded[spm];
    if (spmBlock.expanded[tsm]) {
      const next = { ...expanded };
      delete next[region].expanded[rsm].expanded[spm].expanded[tsm];
      setExpanded(next);
      return;
    }
    const dealer = await dealerApi({
      ...filterdata,
      region: [region],
      Rsm_Name: [rsm],
      Spm_Name: [spm],
      Tsm_Name: [tsm],
    });
    setExpanded((p) => ({
      ...p,
      [region]: {
        ...p[region],
        expanded: {
          ...p[region].expanded,
          [rsm]: {
            ...p[region].expanded[rsm],
            expanded: {
              ...p[region].expanded[rsm].expanded,
              [spm]: {
                ...p[region].expanded[rsm].expanded[spm],
                expanded: { ...spmBlock.expanded, [tsm]: dealer },
              },
            },
          },
        },
      },
    }));
  };

  /* ================= MODAL ================= */

  const openDetailModal = async (extraFilters) => {
    setModalFilters(extraFilters);
    setOpen(true);
    setDetailLoading(true);
  };

  /* ================= ROW ================= */
  const renderRow = (name, d, arrow, onToggle, bg, modalFilters) => (
    <tr
      key={name}
      className={`${bg} cursor-pointer`}
      onClick={() => openDetailModal(modalFilters)}
    >
      <td
        onClick={(e) => {
          e.stopPropagation();
          onToggle && onToggle();
        }}
        className={`pl-3 py-[2px] text-xs lg:text-sm border border-gray-300 h-[30px] relative flex items-center gap-2 whitespace-nowrap`}
      >
        {name} {arrow}
      </td>
      <td className="p-[2px] text-xs lg:text-sm border border-gray-300 h-[30px] relative">
        {d.TOTAL_NFA_RAISED}
      </td>
      <td className="p-[2px] text-xs lg:text-sm border border-gray-300 h-[30px] relative">
        {d.NFA_Approved}
      </td>
      <td className="p-[2px] text-xs lg:text-sm border border-gray-300 h-[30px] relative">
        {d.NFA_Pending}
      </td>
      <td className="p-[2px] text-xs lg:text-sm border border-gray-300 h-[30px] relative">
        {d.NFA_NOT_RETAILED}
      </td>
      <td className="p-[2px] text-xs lg:text-sm border border-gray-300 h-[30px] relative">
        {d.NFA_Partial}
      </td>
    </tr>
  );

  const totalRow = Object.values(data).reduce(
    (acc, cur) => {
      acc.TOTAL_NFA_RAISED += cur.TOTAL_NFA_RAISED || 0;
      acc.NFA_Approved += cur.NFA_Approved || 0;
      acc.NFA_Pending += cur.NFA_Pending || 0;
      acc.NFA_NOT_RETAILED += cur.NFA_NOT_RETAILED || 0;
      acc.NFA_Partial += cur.NFA_Partial || 0;
      return acc;
    },
    {
      TOTAL_NFA_RAISED: 0,
      NFA_Approved: 0,
      NFA_Pending: 0,
      NFA_NOT_RETAILED: 0,
      NFA_Partial: 0,
    },
  );

  /* ================= UI ================= */
  return (
    <div className="p-2">
      <h2 className="text-sm font-semibold mb-2">NFA Status</h2>

      <div className="h-[210px] md:w-[420px] w-full md overflow-auto">
        {isLoading ? (
          <div className="grid place-items-center h-full">
            <Loader />
          </div>
        ) : (
          <>
            <WaterMark />
            <table className="w-full text-xs border-collapse text-center">
              <thead className="sticky text-white top-0 bg-[#2A5775] text-left z-[1]">
                <tr>
                  <th className="px-1 text-sm py-2 text-center font-medium border border-[#355f7a] whitespace-nowrap">
                    Name
                  </th>
                  <th className="px-1 text-sm py-2 text-center font-medium border border-[#355f7a] whitespace-nowrap">
                    Raised
                  </th>
                  <th className="px-1 text-sm py-2 text-center font-medium border border-[#355f7a] whitespace-nowrap">
                    Approved
                  </th>
                  <th className="px-1 text-sm py-2 text-center font-medium border border-[#355f7a] whitespace-nowrap">
                    Pending Approval
                  </th>
                  <th className="px-1 text-sm py-2 text-center font-medium border border-[#355f7a] whitespace-nowrap">
                    Approved (Not Retailed)
                  </th>
                  <th className="px-1 text-sm py-2 text-center font-medium border border-[#355f7a] whitespace-nowrap">
                    Approved (Partially Retailed)
                  </th>
                </tr>
              </thead>
              <tbody>
                {REGION_ORDER.filter((region) => data[region]).map((region) => {
                  const r = data[region];

                  return (
                    <React.Fragment key={region}>
                      {renderRow(
                        region,
                        r,
                        expanded[region] ? (
                          <IoMdArrowDropup />
                        ) : (
                          <IoMdArrowDropdown />
                        ),
                        () => toggleRegion(region),
                        "even:bg-gray-200 odd:bg-white",
                        { region: [region] },
                      )}

                      {expanded[region] &&
                        Object.entries(expanded[region].rsm).map(([rsm, s]) => (
                          <React.Fragment key={rsm}>
                            {renderRow(
                              rsm,
                              s,
                              expanded[region].expanded[rsm] ? (
                                <IoMdArrowDropup />
                              ) : (
                                <IoMdArrowDropdown />
                              ),
                              () => toggleRsm(region, rsm),
                              "bg-[#2a5775] text-white",
                              { region: [region], Rsm_Name: [rsm] },
                            )}

                            {expanded[region].expanded[rsm] &&
                              Object.entries(
                                expanded[region].expanded[rsm].spm,
                              ).map(([spm, t]) => (
                                <React.Fragment key={spm}>
                                  {renderRow(
                                    spm,
                                    t,
                                    expanded[region].expanded[rsm].expanded[
                                      spm
                                    ] ? (
                                      <IoMdArrowDropup />
                                    ) : (
                                      <IoMdArrowDropdown />
                                    ),
                                    () => toggleSpm(region, rsm, spm),
                                    "bg-slate-100",
                                    {
                                      region: [region],
                                      Rsm_Name: [rsm],
                                      Spm_Name: [spm],
                                    },
                                  )}

                                  {expanded[region].expanded[rsm].expanded[
                                    spm
                                  ] &&
                                    Object.entries(
                                      expanded[region].expanded[rsm].expanded[
                                        spm
                                      ].tsm,
                                    ).map(([tsm, d]) => (
                                      <React.Fragment key={tsm}>
                                        {renderRow(
                                          tsm,
                                          d,
                                          expanded[region].expanded[rsm]
                                            .expanded[spm].expanded[tsm] ? (
                                            <IoMdArrowDropup />
                                          ) : (
                                            <IoMdArrowDropdown />
                                          ),
                                          () =>
                                            toggleTsm(region, rsm, spm, tsm),
                                          "bg-slate-300",
                                          {
                                            region: [region],
                                            Rsm_Name: [rsm],
                                            Spm_Name: [spm],
                                            Tsm_Name: [tsm],
                                          },
                                        )}

                                        {expanded[region].expanded[rsm]
                                          .expanded[spm].expanded[tsm] &&
                                          Object.entries(
                                            expanded[region].expanded[rsm]
                                              .expanded[spm].expanded[tsm],
                                          ).map(([dealer, d]) =>
                                            renderRow(
                                              dealer,
                                              d,
                                              null,
                                              null,
                                              "bg-slate-400",
                                              {
                                                region: [region],
                                                Rsm_Name: [rsm],
                                                Spm_Name: [spm],
                                                Tsm_Name: [tsm],
                                                Dealer_Name: [dealer],
                                              },
                                            ),
                                          )}
                                      </React.Fragment>
                                    ))}
                                </React.Fragment>
                              ))}
                          </React.Fragment>
                        ))}
                    </React.Fragment>
                  );
                })}

                {/* TOTAL ROW */}
                <tr className="sticky bottom-0 bg-[#2A5775] text-white font-semibold">
                  <td className="border pl-3 text-left">TOTAL</td>
                  <td className="border">{totalRow.TOTAL_NFA_RAISED}</td>
                  <td className="border">{totalRow.NFA_Approved}</td>
                  <td className="border">{totalRow.NFA_Pending}</td>
                  <td className="border">{totalRow.NFA_NOT_RETAILED}</td>
                  <td className="border">{totalRow.NFA_Partial}</td>
                </tr>
              </tbody>
            </table>

            <p className="text-[11px] mt-2 flex items-center gap-1">
              <TbHandClick size={14} /> Click any row to open detailed report
            </p>
          </>
        )}
      </div>

      {open && (
        <ModalMuiTable
          open={open}
          heading="Nfa Detailed Report"
          onClose={() => setOpen(false)}
          apiEndpoint={"/realization/nfa_detailed_report"}
          nfaBody={modalFilters}
          showFilterButton={false}
        />
      )}
    </div>
  );
};

export default Nfa;
